/**
 * Helpers compartidos de D-Talk (estados, elegibilidad, colores de aspiración).
 * Reusa avatar/period helpers del Mapa de Talento.
 */
import type { DTalkProcessStatus, DTalkEligibility } from '../../../../../shared/types/dtalk';
import { DTALK_DISPLAY_STATUSES, dtalkDisplayStatus, type DTalkDisplayStatus } from '../../../../../shared/dtalk-stats';
export { getInitials, getAvatarColor, formatPeriodLabel } from '../talent-map/talentMapShared';

export type { DTalkProcessStatus, DTalkEligibility } from '../../../../../shared/types/dtalk';

interface StatusConf { labelKey: string; badge: string; dot: string; }

/** Config por estado del proceso (§13). `labelKey` → i18n `performance.dtalk.status.*`. */
export const DTALK_STATUS: Record<DTalkProcessStatus, StatusConf> = {
  not_started:        { labelKey: 'performance.dtalk.status.not_started',        badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300', dot: 'bg-slate-400' },
  in_progress:        { labelKey: 'performance.dtalk.status.in_progress',        badge: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',   dot: 'bg-blue-500' },
  paused:             { labelKey: 'performance.dtalk.status.paused',             badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  pending_validation: { labelKey: 'performance.dtalk.status.pending_validation', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  completed:          { labelKey: 'performance.dtalk.status.completed',          badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  reopened:           { labelKey: 'performance.dtalk.status.reopened',           badge: 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300', dot: 'bg-violet-500' },
  closed_window:      { labelKey: 'performance.dtalk.status.closed_window',      badge: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', dot: 'bg-slate-400' },
  not_eligible:       { labelKey: 'performance.dtalk.status.not_eligible',       badge: 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-300',       dot: 'bg-red-400' },
};

/** Los 4 estados visibles (Flor 07/10/2026): opciones de filtro, barras, CSV. */
export const DTALK_DISPLAY_OPTIONS: readonly DTalkDisplayStatus[] = DTALK_DISPLAY_STATUSES;

/**
 * Chip de una fila: uno de los 4 estados visibles o, si la persona no es
 * elegible, su elegibilidad (Licencia / Onboarding / Inactiva) en gris.
 */
export function dtalkChip(processStatus: string | null | undefined, eligibilityStatus?: string | null): StatusConf {
  const d = dtalkDisplayStatus(processStatus);
  if (d) return DTALK_STATUS[d];
  // Sin motivo de elegibilidad conocido (o "elegible" con estado no elegible): "No elegible" a secas.
  if (!eligibilityStatus || eligibilityStatus === 'eligible') return DTALK_STATUS.not_eligible;
  const elig = eligibilityStatus as DTalkEligibility;
  return { labelKey: DTALK_ELIGIBILITY_LABEL[elig] ?? DTALK_STATUS.not_eligible.labelKey, badge: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400', dot: 'bg-slate-400' };
}

export const DTALK_ELIGIBILITY_LABEL: Record<DTalkEligibility, string> = {
  eligible: 'performance.dtalk.eligibility.eligible',
  onboarding: 'performance.dtalk.eligibility.onboarding',
  license: 'performance.dtalk.eligibility.license',
  inactive: 'performance.dtalk.eligibility.inactive',
};

/** Color para chips/donut de aspiración principal (match por keyword, robusto a texto libre). */
export function aspirationColor(aspiracion?: string | null): string {
  const s = (aspiracion || '').toLowerCase();
  if (!s) return '#cbd5e1';                              // slate-300 — sin dato
  if (s.includes('vertical')) return '#14b8a6';         // teal-500
  if (s.includes('horizontal')) return '#60a5fa';       // blue-400
  if (s.includes('especial') || s.includes('profundiz')) return '#fbbf24'; // amber-400
  if (s.includes('área') || s.includes('area')) return '#a78bfa'; // violet-400
  if (s.includes('sin') || s.includes('explora')) return '#f87171'; // red-400 — sin claridad
  return '#94a3b8';                                      // slate-400 — otra
}

/** Normaliza una aspiración libre a una categoría corta para tendencias/insights. */
export function aspirationBucket(aspiracion?: string | null): string {
  const s = (aspiracion || '').toLowerCase();
  if (!s) return 'Sin dato';
  if (s.includes('vertical')) return 'Crecimiento vertical';
  if (s.includes('horizontal')) return 'Crecimiento horizontal';
  if (s.includes('especial') || s.includes('profundiz')) return 'Especialización';
  if (s.includes('área') || s.includes('area')) return 'Cambio de área';
  if (s.includes('sin') || s.includes('explora')) return 'Sin claridad';
  return 'Otra';
}
